// ============================================================
// DataTable.js
// Generic table with pagination, loading/error/empty states, and
// optional row click. The shape matches the Workstream 1 admin
// pagination response { items, totalCount, page, pageSize }.
//
// Search and column-based filters live ABOVE the table at the
// page level — DataTable doesn't own them. The page passes the
// filtered/paginated `items` array down; DataTable just renders.
//
// Pagination model: page is 1-indexed. The component renders
// "Page X of Y" with Prev/Next buttons. Calls onPageChange(page)
// with the new page number when a button is clicked.
//
// Column shape:
//   {
//     key:        'unique-id',        // for React key on cells
//     label:      'Header text',      // header cell content
//     render:     (row) => ReactNode, // cell body for this column
//     width?:     '120px' | '15%',    // optional fixed/percent width
//     align?:     'left'|'right'|'center', // text alignment, default left
//
//     // ── Mobile card mode (all optional) ──
//     mobile?:      'title' | 'subtitle' | 'badge' | 'meta' | 'hide',
//     mobileLabel?: 'Shorter label',  // defaults to `label`
//   }
//
// ── Why a card mode ──────────────────────────────────────
// A nine-column table on a 390px phone is a table seen through a
// slot: Trial Requests measured 1,069px of columns inside a 116px
// window. Below --breakpoint-md the same rows render as cards
// instead, so every field is readable without horizontal scrolling.
//
// The `mobile` role is opt-in and degrades safely: a column that
// declares nothing renders as a labelled meta row, and if no column
// claims 'title' the first column is promoted. So all 17 existing
// consumers get a usable card list with no change at all, and the
// pages worth tuning can tune.
// ============================================================

import React from 'react';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle, RotateCw } from 'lucide-react';
import useMediaQuery from '../hooks/useMediaQuery';
import StatusMessage from './StatusMessage';
import './DataTable.css';

// Keep in lockstep with --breakpoint-md in index.css.
export const CARD_MODE_QUERY = '(max-width: 768px)';

// A click inside a cell's own control (Revoke, a toggle, a link) must
// not also fire the row navigation — several consumers put buttons in
// cells. Guards the card's whole-surface click; the title button is
// unaffected because it IS the intended target.
function isInteractiveTarget(target) {
  return Boolean(target.closest && target.closest('button, a, input, select, textarea, [role="button"]'));
}

export default function DataTable({
  data,
  columns,
  rowKey,
  onRowClick,
  totalCount,
  page,
  pageSize,
  onPageChange,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = 'No results.',
  emptyAction = null,
}) {
  const cardMode = useMediaQuery(CARD_MODE_QUERY);

  const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const visible = columns.filter((c) => c.mobile !== 'hide');
  const titleCol = visible.find((c) => c.mobile === 'title') || visible[0];
  const subtitleCols = visible.filter((c) => c.mobile === 'subtitle');
  const badgeCols = visible.filter((c) => c.mobile === 'badge');
  const metaCols = visible.filter(
    (c) => c !== titleCol && c.mobile !== 'subtitle' && c.mobile !== 'badge',
  );

  // Shared by both renderers so the two can never disagree about what
  // "loading" or "no results" looks like. StatusMessage keeps the
  // rendered markup byte-identical (same div, same classes) and adds
  // the WCAG 4.1.3 announcement — this one component covers every
  // DataTable consumer's loading/error/empty states at once.
  function stateContent() {
    if (loading && data.length === 0) {
      return (
        <StatusMessage variant="loading" className="data-table__state">
          <Loader2 size={16} className="data-table__spinner" />
          <span>Loading…</span>
        </StatusMessage>
      );
    }
    if (!loading && error) {
      return (
        <StatusMessage variant="error" className="data-table__state data-table__state--error">
          <AlertCircle size={16} />
          <span>{error}</span>
          {onRetry && (
            <button type="button" className="data-table__retry" onClick={onRetry}>
              <RotateCw size={14} />
              Retry
            </button>
          )}
        </StatusMessage>
      );
    }
    if (!loading && !error && data.length === 0) {
      return (
        <StatusMessage variant="empty" className="data-table__state data-table__state--empty">
          <span>{emptyMessage}</span>
          {emptyAction && <div className="data-table__empty-action">{emptyAction}</div>}
        </StatusMessage>
      );
    }
    return null;
  }

  const state = stateContent();

  function renderCards() {
    return (
      <ul className="data-table__cards">
        {data.map((row) => {
          const title = titleCol ? titleCol.render(row) : null;
          return (
            <li
              key={rowKey(row)}
              className={`data-table__card ${onRowClick ? 'data-table__card--clickable' : ''}`}
              onClick={onRowClick ? (e) => {
                if (!isInteractiveTarget(e.target)) onRowClick(row);
              } : undefined}
            >
              <div className="data-table__card-head">
                {onRowClick ? (
                  <button
                    type="button"
                    className="data-table__card-title data-table__card-title--btn"
                    onClick={() => onRowClick(row)}
                  >
                    {title}
                  </button>
                ) : (
                  <div className="data-table__card-title">{title}</div>
                )}
                {badgeCols.length > 0 && (
                  <div className="data-table__card-badges">
                    {badgeCols.map((col) => (
                      <React.Fragment key={col.key}>{col.render(row)}</React.Fragment>
                    ))}
                  </div>
                )}
              </div>

              {subtitleCols.map((col) => (
                <div key={col.key} className="data-table__card-subtitle">{col.render(row)}</div>
              ))}

              {metaCols.length > 0 && (
                <dl className="data-table__card-meta">
                  {metaCols.map((col) => (
                    <div key={col.key} className="data-table__card-meta-row">
                      <dt>{col.mobileLabel || col.label}</dt>
                      <dd>{col.render(row)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  function renderTable() {
    return (
      <div className="data-table__scroll">
        <table className="data-table__table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    width: col.width,
                    textAlign: col.align || 'left',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {state && (
              <tr className="data-table__state-row">
                <td colSpan={columns.length}>{state}</td>
              </tr>
            )}

            {!error && data.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? 'data-table__row data-table__row--clickable' : 'data-table__row'}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} style={{ textAlign: col.align || 'left' }}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={`data-table ${cardMode ? 'data-table--cards' : ''}`}>
      {cardMode
        ? (
          <>
            {state && <div className="data-table__card-state">{state}</div>}
            {!error && data.length > 0 && renderCards()}
          </>
        )
        : renderTable()}

      {totalCount > 0 && (
        <div className="data-table__pagination">
          <span className="data-table__page-info">
            Page {page} of {totalPages} · {totalCount} total
          </span>
          <div className="data-table__page-buttons">
            <button
              type="button"
              className="data-table__page-btn"
              onClick={() => onPageChange(page - 1)}
              disabled={!canPrev}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <button
              type="button"
              className="data-table__page-btn"
              onClick={() => onPageChange(page + 1)}
              disabled={!canNext}
              aria-label="Next page"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
